import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    Button,
    Divider,
    Chip,
    Alert,
    CircularProgress,
    IconButton,
    Paper,
    alpha
} from "@mui/material";
import {
    VerifiedUser,
    ErrorOutline,
    CloudUpload,
    VpnKey,
    Business,
    Schedule,
    Computer,

} from "@mui/icons-material";
import { toast } from "react-toastify";
import api from "../services/api";

const LicenseManagementPage = () => {
    const { t } = useTranslation();
    const [license, setLicense] = useState(null);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);

    const fetchLicenseStatus = async () => {
        setLoading(true);
        try {
            const response = await api.get("/api/license/status");
            setLicense(response.data);
            setError(null);
        } catch (err) {
            setError(err.response?.data?.message || t('license.failedToFetch'));
            setLicense(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLicenseStatus();
    }, []);

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (!file.name.endsWith(".xml")) {
            toast.error(t('license.invalidFileType'));
            return;
        }

        const formData = new FormData();
        formData.append("license", file);

        setUploading(true);
        try {
            await api.post("/api/license/upload", formData);
            toast.success(t('license.updatedSuccessfully'));
            fetchLicenseStatus();
        } catch (err) {
            toast.error(err.response?.data?.message || t('license.failedToUpload'));
        } finally {
            setUploading(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 1000, mx: "auto", py: 4 }}>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
                {t('license.licenseManagement')}
            </Typography>
            <Typography variant="body1" color="textSecondary" mb={4}>
                {t('license.monitorAndUpdate')}
            </Typography>

            <Grid container spacing={3}>
                {/* Status Card */}
                <Grid item xs={12} md={8}>
                    <Card elevation={3} sx={{ borderRadius: 3 }}>
                        <CardContent sx={{ p: 4 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                                <Typography variant="h6" fontWeight="bold">
                                    {t('license.systemStatus')}
                                </Typography>
                                {license ? (
                                    <Chip
                                        icon={<VerifiedUser fontSize="small" />}
                                        label={t('license.licensed')}
                                        color="success"
                                        variant="outlined"
                                        sx={{ fontWeight: "bold" }}
                                    />
                                ) : (
                                    <Chip
                                        icon={<ErrorOutline fontSize="small" />}
                                        label={t('license.unlicensed')}
                                        color="error"
                                        variant="outlined"
                                        sx={{ fontWeight: "bold" }}
                                    />
                                )}
                            </Box>

                            {license ? (
                                <Grid container spacing={4}>
                                    <Grid item xs={12} sm={6}>
                                        <Box sx={{ display: "flex", alignItems: "center" }}>
                                            <IconButton disabled sx={{ bgcolor: "primary.main", color: "white", mr: 2 }}>
                                                <VpnKey />
                                            </IconButton>
                                            <Box>
                                                <Typography variant="caption" color="textSecondary">{t('license.licenseEdition')}</Typography>
                                                <Typography variant="body1" fontWeight="bold">{license.edition}</Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Box sx={{ display: "flex", alignItems: "center" }}>
                                            <IconButton disabled sx={{ bgcolor: "secondary.main", color: "white", mr: 2 }}>
                                                <Business />
                                            </IconButton>
                                            <Box>
                                                <Typography variant="caption" color="textSecondary">{t('license.customer')}</Typography>
                                                <Typography variant="body1" fontWeight="bold">{license.customer}</Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Box sx={{ display: "flex", alignItems: "center" }}>
                                            <IconButton disabled sx={{ bgcolor: "warning.main", color: "white", mr: 2 }}>
                                                <Schedule />
                                            </IconButton>
                                            <Box>
                                                <Typography variant="caption" color="textSecondary">{t('license.expiryDate')}</Typography>
                                                <Typography variant="body1" fontWeight="bold">
                                                    {new Date(license.expiresAt).toLocaleDateString()}
                                                </Typography>
                                                <Typography variant="caption" color={license.daysRemaining < 30 ? "error.main" : "success.main"}>
                                                    {t('license.daysRemaining', { count: license.daysRemaining })}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Box sx={{ display: "flex", alignItems: "center" }}>
                                            <IconButton disabled sx={{ bgcolor: "info.main", color: "white", mr: 2 }}>
                                                <Computer />
                                            </IconButton>
                                            <Box>
                                                <Typography variant="caption" color="textSecondary">{t('license.machineFingerprint')}</Typography>
                                                <Typography variant="body2" sx={{ wordBreak: "break-all", fontFamily: "monospace", fontSize: "0.75rem" }}>
                                                    {license.machineFingerprint || t('license.notBound')}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                </Grid>
                            ) : (
                                <Alert severity="error" variant="filled" sx={{ borderRadius: 2 }}>
                                    {t('license.licenseWarning')}
                                </Alert>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Upload Card */}
                <Grid item xs={12} md={4}>
                    <Card elevation={3} sx={{ borderRadius: 3, textAlign: "center", height: "100%" }}>
                        <CardContent sx={{ p: 4, display: "flex", flexDirection: "column", justifyContent: "center", height: "100%" }}>
                            <Typography variant="h6" fontWeight="bold" mb={2}>
                                {t('license.updateLicense')}
                            </Typography>
                            <Paper
                                variant="outlined"
                                sx={{
                                    py: 4,
                                    px: 2,
                                    borderStyle: "dashed",
                                    bgcolor: (theme) => theme.palette.mode === 'light' ? '#f8fafc' : alpha(theme.palette.primary.main, 0.05),
                                    mb: 3
                                }}
                            >
                                <CloudUpload sx={{ fontSize: 48, color: "text.secondary", mb: 2 }} />
                                <Typography variant="body2" color="textSecondary">
                                    {t('license.uploadInstruction')}
                                </Typography>
                            </Paper>
                            <Button
                                variant="contained"
                                component="label"
                                fullWidth
                                disabled={uploading}
                                startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <CloudUpload />}
                            >
                                {uploading ? t('license.uploading') : t('license.uploadLicense')}
                                <input type="file" hidden accept=".xml" onChange={handleFileUpload} />
                            </Button>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default LicenseManagementPage;
